import { Flex } from "antd"
import React from "react"

import { type LeadWorkspaceTabContext } from "../lead-workspace-tabs.tsx"
import { LeadScopeDescriptionCard } from "./LeadScopeDescriptionCard.tsx"
import { LeadScopeMilestonesCard } from "./LeadScopeMilestonesCard.tsx"
import { LeadScopePlanningCard } from "./LeadScopePlanningCard.tsx"
import { LeadScopeProjectTypeCard } from "./LeadScopeProjectTypeCard.tsx"

type LeadScopeTabProps = Pick<
  LeadWorkspaceTabContext,
  "lead" | "canEdit" | "filterOptions" | "onPatch"
>

/**
 * The PRD's Scope tab, laid out per the client's mockup (2026-08-30) as one
 * "Project Scope & Planning" workspace rather than a stack of full-width
 * cards. Project Type and Project Planning stack in a ~1/3-width left
 * column — Planning reads as a compact companion to Project Type, not its
 * own full-width strip — beside the wider Scope & Description on the right,
 * stretched to match the left column's combined height. The Financial
 * Summary and Milestones keep the full width below: both benefit from the
 * horizontal room (the donut/insight row, the milestone table's columns).
 * Every field here is also a lead field somewhere else in the workspace, so
 * each card still writes it through the one shared lead PATCH; only where
 * the cards sit on the page changed.
 */
function LeadScopeTab({
  lead,
  canEdit,
  filterOptions,
  onPatch
}: LeadScopeTabProps) {
  return (
    <Flex vertical gap={"middle"} data-testid={"lead-scope"}>
      <div
        className={
          "grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-4 items-stretch"
        }
      >
        <Flex vertical gap={"middle"} className={"min-w-0"}>
          <LeadScopeProjectTypeCard
            lead={lead}
            filterOptions={filterOptions}
            canEdit={canEdit}
            onPatch={onPatch}
          />
          <LeadScopePlanningCard
            lead={lead}
            filterOptions={filterOptions}
            canEdit={canEdit}
            onPatch={onPatch}
          />
        </Flex>

        <LeadScopeDescriptionCard
          lead={lead}
          canEdit={canEdit}
          onPatch={onPatch}
        />
      </div>

      <LeadScopeMilestonesCard
        lead={lead}
        filterOptions={filterOptions}
        canEdit={canEdit}
        onPatch={onPatch}
      />
    </Flex>
  )
}

export { LeadScopeTab }
